import React from 'react';
import { Navigate } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { ProtectedRoute } from '../../components/ProtectedRoute';

const HRDashboard = React.lazy(() => import('../../pages/HR/HRDashboard'));
const EmployeeList = React.lazy(() => import('../../pages/HR/EmployeeList'));
const EmployeeDetail = React.lazy(() => import('../../pages/HR/EmployeeDetail'));
// OfferManagement was removed with the pre-hire pipeline — see the redirect
// on the 'offers' path below.
const Policies = React.lazy(() => import('../../pages/HR/Policies'));
const BulkNotices = React.lazy(() => import('../../pages/HR/BulkNotices'));
const OnboardingTemplates = React.lazy(() => import('../../pages/HR/OnboardingTemplates'));
const BackgroundCheckPackages = React.lazy(() => import('../../pages/HR/BackgroundCheckPackages'));
const AuditLog = React.lazy(() => import('../../pages/HR/AuditLog'));
const BulkSetWorkEmails = React.lazy(() => import('../../pages/HR/BulkSetWorkEmails'));
const OrgChartPage = React.lazy(() => import('../../pages/HR/OrgChart/OrgChartPage'));
const SeatDetailPage = React.lazy(() => import('../../pages/HR/OrgChart/SeatDetailPage'));
const OrgChartPrintPage = React.lazy(() => import('../../pages/HR/OrgChart/OrgChartPrintPage'));
const ModuleAgentFeed = React.lazy(() =>
  import('../../components/shared/ModuleAgentFeed').then(m => ({ default: m.ModuleAgentFeed }))
);

export const routes: RouteObject[] = [
  { index: true, element: <ProtectedRoute requireHrRole><HRDashboard /></ProtectedRoute> },
  { path: 'employees', element: <ProtectedRoute requireHrRole><EmployeeList /></ProtectedRoute> },
  { path: 'employees/:employeeId', element: <ProtectedRoute requireHrRole><EmployeeDetail /></ProtectedRoute> },
  // Onboarding is run from the employee: New Employee on the Employees page, then the employee's Onboarding tab.
  // The old in-progress list is gone; the redirect keeps bookmarks and emailed links useful.
  { path: 'onboarding', element: <ProtectedRoute requireHrRole><Navigate to="/hr/employees" replace /></ProtectedRoute> },
  // The HR offers page was a view over employees in `candidate`/`offer_sent`
  // status — a second, thinner copy of a pipeline the Recruiting module
  // already owns properly. Redirected rather than deleted outright so old
  // links, bookmarks and any lingering nav entry land somewhere useful.
  // Kept behind requireHrRole like every other route in this file. The
  // redirect target is separately gated, so this is defence in depth rather
  // than the only check — but a bare route here would be the one unguarded
  // entry in the module.
  {
    path: 'offers',
    element: (
      <ProtectedRoute requireHrRole>
        <Navigate to="/recruiting/pipeline" replace />
      </ProtectedRoute>
    ),
  },
  { path: 'org-chart', element: <ProtectedRoute requireHrRole><OrgChartPage /></ProtectedRoute> },
  { path: 'org-chart/print', element: <ProtectedRoute requireHrRole><OrgChartPrintPage /></ProtectedRoute> },
  { path: 'seats/:seatId', element: <ProtectedRoute requireHrRole><SeatDetailPage /></ProtectedRoute> },
  { path: 'policies', element: <ProtectedRoute requireHrRole><Policies /></ProtectedRoute> },
  // #5801: distributing a notice is hr_admin only on the server (hr_distributePolicy).
  { path: 'notices', element: <ProtectedRoute requireHrRole="hr_admin"><BulkNotices /></ProtectedRoute> },
  // The Approvals page was replaced by the Pending Approvals card on the HR dashboard; the redirect keeps old links
  // (and older emails) landing where the requests now are.
  { path: 'approvals', element: <ProtectedRoute requireHrRole><Navigate to="/hr" replace /></ProtectedRoute> },
  { path: 'templates', element: <ProtectedRoute requireHrRole><OnboardingTemplates /></ProtectedRoute> },
  { path: 'bg-packages', element: <ProtectedRoute requireHrRole="hr_admin"><BackgroundCheckPackages /></ProtectedRoute> },
  { path: 'audit', element: <ProtectedRoute requireHrRole="hr_admin"><AuditLog /></ProtectedRoute> },
  { path: 'admin/bulk-set-work-emails', element: <ProtectedRoute requireHrRole="hr_admin"><BulkSetWorkEmails /></ProtectedRoute> },
  { path: 'agent-activity', element: <ProtectedRoute requireHrRole><ModuleAgentFeed moduleId="P9" /></ProtectedRoute> },
];
